import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { List, Row, Col, Button, Alert, Modal, App, Upload, Tooltip, Progress } from 'antd';
import {
  InboxOutlined,
  LoadingOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import { OrganizationImportStore, OrganizationItemStore, OrganizationSummaryStore } from 'src/stores/organizations-store';
import * as Moment from 'moment';
import { AuditLogSummary, AuditLogSummaryStore } from 'src/stores/audit-logs-store';

// @ts-ignore
import initSqlJs from 'sql.js';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
import sqlWasm from '!!file-loader?name=sql-wasm-[contenthash].wasm!sql.js/dist/sql-wasm.wasm';
import { getDateFormat } from 'src/common/utils';
import i18next from 'i18next';

const { Dragger } = Upload;

interface OrganizationImportProps {
  onClose: () => void;
  onUploadStart: (promise: any) => {};
}
enum FileState {
  Added = 'Added',
  Uploading = 'Uploading',
  Uploaded = 'Uploaded',
  Error = 'Error',
}
type CustomFile = { file: RcFile; state: FileState; error: string };

type OrganizationImportComponentProps = OrganizationImportProps;

enum BulkImportProcessState {
  PreUpload,
  Uploading,
  PostUpload,
}

const OrganizationImportModal: FC<OrganizationImportComponentProps> = (props) => {
  const { onClose } = props;
  const { t } = useTranslation();

  const organizationItemStore = container.get(OrganizationItemStore);

  const [processState, setProcessState] = useState<BulkImportProcessState>(BulkImportProcessState.PreUpload);
  const [customError, setCustomError] = useState<string>('');
  const [files, setFiles] = useState<CustomFile[]>([]);
  const [checkingFile, setCheckingFile] = useState(false);
  const lastImportDate = useRef('');
  const organizationIsLocked = useRef(false);

  const uploaded = files.filter((f) => f.state === FileState.Uploaded).length;
  const notUploaded = files.filter((f) => f.state === FileState.Error).length;
  const percent = Math.round((100 * (uploaded + notUploaded)) / files.length);
  const success = Math.round((100 * uploaded) / files.length);
  const error = Math.round((100 * notUploaded) / files.length);
  const { modal } = App.useApp();

  const uploading = processState === BulkImportProcessState.Uploading;

  const getIcon = (state: FileState, error?: string) => {
    switch (state) {
      case FileState.Added:
        return <DatabaseOutlined />;
      case FileState.Uploading:
        return <LoadingOutlined style={{ color: 'blue' }} />;
      case FileState.Uploaded:
        return <CheckCircleOutlined style={{ color: 'green' }} />;
      case FileState.Error:
        return (error || '').length !== 0 ? (
          <Tooltip title={error}>
            <CloseCircleOutlined style={{ color: 'red' }} />
          </Tooltip>
        ) : (
          <CloseCircleOutlined style={{ color: 'red' }} />
        );
      default:
        return 'Error';
    }
  };

  const onRemove = (file: CustomFile) => {
    if (processState !== BulkImportProcessState.PreUpload) return;
    setFiles(files.filter((f) => f.file.uid !== file.file.uid));
  };

  const checkFieldInDB = async (file: File) => {
    const SQL = await initSqlJs({ locateFile: () => sqlWasm });
    const buffer = await file.arrayBuffer();
    const uInt8Array = new Uint8Array(buffer);
    const db = new SQL.Database(uInt8Array);

    try {
      const result = db.exec('SELECT Id FROM Organizations');
      const field = result[0].values[0][0];
      const organizationsStore = container.get(OrganizationSummaryStore);
      const logsStore = container.get(AuditLogSummaryStore);
      const logType = "'RestoreBackupStart'";

      const logResult = (await logsStore.getLogs({
        searchQuery: '',
        orderBy: [{ field: 'timestamp', direction: 'Descending', useProfile: false }],
        filter: [`organizationId eq ${field} and type eq ${logType}`],
        skip: 0,
        take: 50,
      })) as AuditLogSummary[];

      let organizationExists = false;

      if (logResult.length > 0) {
        const importDate = Moment.utc(logResult[0].timestamp).locale(i18next.language).local().format(`${getDateFormat()}, hh:mm a`);
        lastImportDate.current = importDate;
        organizationExists = true;
      } else {
        organizationExists = await organizationsStore.organizationExists(field);
      }

      if (organizationExists) {
        organizationIsLocked.current = await organizationItemStore.checkLockedStatus(field);
      } else {
        organizationIsLocked.current = false;
      }

      return organizationExists;
    } catch (error) {
      console.log(error);
      return '';
    }
  };

  const onUpload = async () => {
    if (processState !== BulkImportProcessState.PreUpload) return;
    if (files.length === 0) return;
    const organizationExists = await checkOrganizationBeforeUpload(files[0].file);
    if (organizationExists) {
      modal.confirm({
        title: (
          <>
            <span>{t(organizationIsLocked.current ? 'OrganizationIsLocked' : 'RestoreOrganizationConfirmDialog')}</span>
            {lastImportDate.current && (
              <>
                <br />
                <br />
                <strong>{`${t('LastImportDate')} ${lastImportDate.current}`}</strong>
              </>
            )}
          </>
        ),
        cancelButtonProps: { type: 'default' },
        okButtonProps: { disabled: organizationIsLocked.current },
        onCancel: () => {},
        onOk: () => uploadFile(files[0].file),
      });
    } else {
      uploadFile(files[0].file);
    }
  };

  const uploadFile = async (file: File) => {
    const organiationUploadStore = container.get<OrganizationImportStore>(OrganizationImportStore);
    const importPromise = organiationUploadStore.uploadOrganizationBackup(file);
    //const importPromise = new Promise((resolve) => setTimeout(resolve, 3000));
    props.onUploadStart(importPromise);
  };

  const checkOrganizationBeforeUpload = async (file: File) => {
    setCheckingFile(true);
    setProcessState(BulkImportProcessState.Uploading);
    const result = await checkFieldInDB(file);
    setCheckingFile(false);
    setProcessState(BulkImportProcessState.PreUpload);
    return result;
  };

  return (
    <Modal
      className="figma-modal"
      title={<h3 className="figma-modal__title">{t('UploadOrganizationBackup')}</h3>}
      open={true}
      onCancel={onClose}
      closable={!uploading}
      maskClosable={!uploading}
      footer={
        processState !== BulkImportProcessState.PostUpload ? (
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Button disabled={uploading || checkingFile} onClick={() => onClose()} className="big-modal-button primary-outline-button">
                {t('Cancel')}
              </Button>
            </Col>
            <Col span={12}>
              <Button disabled={uploading || files.length === 0 || checkingFile} onClick={onUpload} className="big-modal-button" type="primary">
                {t('Upload')}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row gutter={[24, 0]}>
            <Col span={12}></Col>
            <Col span={12}>
              <Button onClick={onClose} className="big-modal-button" type="primary" disabled={checkingFile}>
                {t('OK')}
              </Button>
            </Col>
          </Row>
        )
      }
    >
      {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
      <div style={{ marginBottom: 25 }}>
        <Dragger
          className="upload-with-status"
          data-testid="upload-dragger"
          multiple={false}
          accept=".eiox"
          beforeUpload={(file) => {
            setFiles([{ file: file, state: FileState.Added, error: null as any }]);
            return false;
          }}
          showUploadList={false}
          disabled={processState !== BulkImportProcessState.PreUpload}
        >
          {processState !== BulkImportProcessState.PreUpload ? (
            <>
              <p className="ant-upload-drag-icon">
                {uploading ? (
                  <LoadingOutlined style={{ color: '#b83542' }} />
                ) : (
                  <>
                    {uploaded === files.length && <CheckCircleOutlined style={{ color: 'green' }} />}
                    {uploaded === 0 && <CloseCircleOutlined style={{ color: 'red' }} />}
                    {uploaded > 0 && uploaded < files.length && <WarningOutlined style={{ color: 'orange' }} />}
                  </>
                )}
                <br />
              </p>
              <p className="ant-upload-text">
                {checkingFile ? t('CheckingIfOrganizationExists') : `${t('Processed')}: ${uploaded + notUploaded}/${files.length}`}
              </p>
              <p className="ant-upload-hint">
                {t('Success')}: {`${uploaded} (${success}%)`} {t('Error')}: {`${notUploaded} (${error}%)`}
              </p>
            </>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: uploading ? 'darkgray' : '#b83542' }} />
              </p>
              <p className="ant-upload-text">{t('AddOrganizationFile')}</p>
            </>
          )}
        </Dragger>
        {uploading && <Progress strokeColor="red" percent={percent} success={{ percent: success }} />}
        {files.length !== 0 && (
          <List
            style={{
              maxHeight: '44vh',
              marginTop: 6,
              overflowY: 'auto',
            }}
            bordered={false}
            size="small"
            itemLayout="horizontal"
            split={false}
            dataSource={files}
            renderItem={(file, idx) => (
              <List.Item
                style={{ paddingTop: 2, paddingBottom: idx + 1 === files.length ? 8 : 2 }}
                key={file.file.uid}
                actions={
                  processState !== BulkImportProcessState.PreUpload
                    ? []
                    : [<DeleteOutlined style={{ cursor: 'pointer' }} onClick={() => onRemove(file)} />]
                }
              >
                <List.Item.Meta avatar={getIcon(file.state, file.error)} description={file.file.name} />
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default OrganizationImportModal;
